/* Shade With Shine — Little Shine Motion System v4.0
   Mobile-first, event-driven, compositor-first, performance-safe.
   Motion never blocks navigation, cart, checkout or other commerce actions. */

(() => {
  'use strict';

  const d = document;
  const w = window;
  const root = d.documentElement;
  const $ = (selector, scope = d) => scope.querySelector(selector);
  const $$ = (selector, scope = d) => Array.from(scope.querySelectorAll(selector));

  const reduceQuery = w.matchMedia ? w.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const fineQuery = w.matchMedia ? w.matchMedia('(hover: hover) and (pointer: fine)') : null;
  const coarseQuery = w.matchMedia ? w.matchMedia('(pointer: coarse)') : null;

  const prefersReducedMotion = () => !!(reduceQuery && reduceQuery.matches);
  const hasFinePointer = () => !!(fineQuery && fineQuery.matches);
  const hasCoarsePointer = () => !!(coarseQuery && coarseQuery.matches);

  root.classList.add('sws-motion-ready');
  root.classList.toggle('sws-touch-device', hasCoarsePointer() || !hasFinePointer());

  const activePresses = new Map();
  let revealObserver = null;
  let pageWinkShown = false;

  function safeClosest(target, selector) {
    return target && target.closest ? target.closest(selector) : null;
  }

  function replay(el, cls, ms = 420) {
    if (!el || prefersReducedMotion()) return;

    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);

    w.setTimeout(() => {
      if (el && el.classList) el.classList.remove(cls);
    }, ms);
  }

  function animate(el, frames, options) {
    if (!el || prefersReducedMotion() || typeof el.animate !== 'function') return null;

    try {
      return el.animate(frames, options);
    } catch (_) {
      return null;
    }
  }


  function setInternalNavigationFlag(event) {
    const a = safeClosest(event.target, 'a[href]');
    if (!a || a.target === '_blank' || a.hasAttribute('download')) return;

    const raw = a.getAttribute('href');
    if (!raw || raw[0] === '#' || /^(mailto:|tel:|javascript:)/i.test(raw)) return;

    let url;
    try {
      url = new URL(a.href, w.location.href);
    } catch (_) {
      return;
    }

    if (
      url.origin !== w.location.origin ||
      /\/(checkout|checkouts|account|payments?|wallets?)(\/|$)/i.test(url.pathname)
    ) {
      return;
    }

    try {
      w.sessionStorage.setItem('sws_internal_nav', '1');
    } catch (_) {}
  }

  d.addEventListener('click', setInternalNavigationFlag, { capture: true, passive: true });

  function showPageWink() {
    if (pageWinkShown || prefersReducedMotion() || !d.body) return;

    try {
      if (w.sessionStorage.getItem('sws_internal_nav') !== '1') return;
      w.sessionStorage.removeItem('sws_internal_nav');
    } catch (_) {
      return;
    }

    pageWinkShown = true;

    const wink = d.createElement('span');
    wink.className = 'sws-page-wink';
    wink.textContent = '✦';
    wink.setAttribute('aria-hidden', 'true');
    d.body.appendChild(wink);

    w.setTimeout(() => wink.remove(), 360);
  }

  function ensureRevealObserver() {
    if (prefersReducedMotion() || !('IntersectionObserver' in w)) return null;
    if (revealObserver) return revealObserver;


    revealObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -3% 0px',
        threshold: 0.045
      }
    );

    return revealObserver;
  }

  const REVEAL_SELECTOR = [
    '.section-heading',
    '.category-card',
    '.product-card',
    '.review-card',
    '.why-card',
    '.product-trust-card',
    '.product-benefit',
    '.faq-group',
    '.sws-faq',
    '.cart-summary',
    '.cart-row',
    '.wishlist-product-card'
  ].join(',');

  function registerReveals(scope = d) {
    const observer = ensureRevealObserver();
    const nodes = $$(REVEAL_SELECTOR, scope).filter(el => el.dataset.swsRevealReady !== '1');

    nodes.forEach((el, index) => {
      el.dataset.swsRevealReady = '1';

      if (!observer) {
        el.classList.add('is-visible');
        return;
      }

      el.classList.add('sws-reveal');
      el.style.setProperty('--sws-delay', `${Math.min((index % 4) * 24, 72)}ms`);
      observer.observe(el);
    });
  }

  function initHeader() {
    const header = $('[data-site-header],.site-header');
    if (!header || header.dataset.swsMotionReady === '1') return;


    header.dataset.swsMotionReady = '1';
    let ticking = false;

    const update = () => {
      ticking = false;
      header.classList.toggle('sws-header-scrolled', w.scrollY > 14);
    };

    w.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        w.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  const PRESS_SELECTOR = [
    'button:not([disabled])',
    'a.btn',
    '.quick-add',
    '.icon-btn',
    '.wishlist-btn',
    '.product-wishlist',
    'summary',
    '.site-search__chip',
    '.site-search__result',
    '.gallery-arrow',
    '.gallery-zoom',
    '.product-thumb',
    '[data-gallery-thumb]',
    '[data-qty-plus]',
    '[data-qty-minus]',
    '[data-cart-plus]',
    '[data-cart-minus]',
    '[data-cart-remove]',
    '[role="button"]'
  ].join(',');

  const TOUCH_CARD_SELECTOR = '.category-card,.product-card,.review-card,.why-card,.product-trust-card,.product-benefit';

  function releasePress(pointerId) {
    const item = activePresses.get(pointerId);
    if (!item) return;

    activePresses.delete(pointerId);

    if (item.control && item.control.classList) item.control.classList.remove('sws-pressed');
    if (item.card && item.card.classList) item.card.classList.remove('sws-touching');

  }

  d.addEventListener(
    'pointerdown',
    event => {
      if (prefersReducedMotion()) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const control = safeClosest(event.target, PRESS_SELECTOR);
      const card = !hasFinePointer() ? safeClosest(event.target, TOUCH_CARD_SELECTOR) : null;

      if (!control && !card) return;

      if (control) control.classList.add('sws-pressed');
      if (card) card.classList.add('sws-touching');

      activePresses.set(event.pointerId, { control, card });
    },
    { passive: true, capture: true }
  );

  ['pointerup', 'pointercancel'].forEach(type => {
    d.addEventListener(type, event => releasePress(event.pointerId), { passive: true, capture: true });
  });

  w.addEventListener('blur', () => {
    activePresses.forEach((_, pointerId) => releasePress(pointerId));
  });

  function burst(target, chars = ['✦', '♡', '✧', '☀'], strength = 1) {
    if (prefersReducedMotion() || !target || !d.body) return;

    const r = target.getBoundingClientRect();
    if (!r.width && !r.height) return;

    const x = r.left + r.width / 2;
    const y = r.top + r.height / 2;
    const vectors = [
      [-25, -22, -18],
      [5, -31, 12],
      [27, -16, 24],
      [-13, 24, -10]
    ];

    chars.slice(0, 4).forEach((char, i) => {
      const node = d.createElement('span');
      node.className = 'sws-burst-char';
      node.textContent = char;
      node.style.left = `${x}px`;
      node.style.top = `${y}px`;
      node.style.setProperty('--dx', `${vectors[i][0] * strength}px`);
      node.style.setProperty('--dy', `${vectors[i][1] * strength}px`);
      node.style.setProperty('--dr', `${vectors[i][2]}deg`);

      node.style.animationDelay = `${i * 14}ms`;
      d.body.appendChild(node);
      w.setTimeout(() => node.remove(), 520);
    });
  }

  function microBurst(target) {
    if (!target || prefersReducedMotion()) return;
    burst(target, ['✦', '✧'], 0.58);
  }

  const cartIcon = () => $('a[href*="/cart"][aria-label*="cart" i],[data-cart-open],[data-cart-link]');
  const wishIcon = () => $('[data-wishlist-open]');

  function flyDot(source, destination) {
    if (prefersReducedMotion() || !source || !destination || !d.body) return;

    const a = source.getBoundingClientRect();
    const b = destination.getBoundingClientRect();
    if (!a.width || !b.width) return;

    const sx = a.left + a.width / 2;
    const sy = a.top + a.height / 2;
    const dx = b.left + b.width / 2 - sx;
    const dy = b.top + b.height / 2 - sy;

    const dot = d.createElement('span');
    dot.className = 'sws-cart-dot';
    dot.style.left = `${sx}px`;
    dot.style.top = `${sy}px`;
    d.body.appendChild(dot);

    animate(
      dot,
      [
        { transform: 'translate(-50%,-50%) scale(.72)', opacity: 0 },
        { transform: 'translate(-50%,-50%) scale(1.08)', opacity: 1, offset: 0.16 },
        {
          transform: `translate(calc(-50% + ${dx * 0.46}px),calc(-50% + ${dy * 0.28 - 22}px)) scale(1.02)`,
          opacity: 1,
          offset: 0.48
        },
        { transform: `translate(calc(-50% + ${dx}px),calc(-50% + ${dy}px)) scale(.18)`, opacity: 0 }
      ],
      { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' }
    );

    w.setTimeout(() => dot.remove(), 460);
  }

  d.addEventListener('sws:cart-changed', event => {
    if (prefersReducedMotion()) return;


    const detail = event.detail || {};
    const source = detail.source;
    const cart = cartIcon();

    replay(source, 'sws-success-button', 430);
    replay(cart, 'sws-cart-bounce', 440);
    $$('[data-cart-count]').forEach(el => replay(el, 'sws-count-pop', 400));

    if (cart) burst(cart, ['✦', '☀', '✦', '♡'], 0.9);

    if (
      source &&
      cart &&
      detail.reason !== 'cart-update' &&
      detail.reason !== 'wishlist-add-all'
    ) {
      flyDot(source, cart);
    }
  });

  d.addEventListener('sws:wishlist-changed', event => {
    if (prefersReducedMotion()) return;

    const detail = event.detail || {};
    const source = detail.source;
    const header = wishIcon();

    replay(source, 'sws-heart-bounce', 410);
    replay(header, 'sws-heart-bounce', 410);
    $$('[data-wishlist-count]').forEach(el => replay(el, 'sws-count-pop', 400));

    if (source) {
      burst(
        source,
        detail.added ? ['♡', '♥', '✦', '♡'] : ['✦', '♡', '✧'],
        detail.added ? 1 : 0.72
      );
    }

    if (detail.handle) {
      const escaped = w.CSS && CSS.escape
        ? CSS.escape(String(detail.handle))
        : String(detail.handle).replace(/[^a-zA-Z0-9_-]/g, '');

      $$(`[data-wishlist-toggle="${escaped}"]`).forEach(btn => {
        if (btn !== source) replay(btn, 'sws-wish-sync', 360);
      });
    }
  });

  d.addEventListener('sws:cart-section-updated', () => {
    const cartRoot = $('[data-cart-section]');
    if (!cartRoot || prefersReducedMotion()) return;


    replay(cartRoot, 'sws-cart-refresh', 390);
    registerReveals(cartRoot);
  });

  d.addEventListener('sws:variant-changed', event => {
    const form = event.detail && event.detail.form;
    if (!form || prefersReducedMotion()) return;

    replay(form.closest('.product-info'), 'sws-variant-settle', 280);
  });

  d.addEventListener('sws:buy-now', event => {
    replay(event.detail && event.detail.source, 'sws-buy-launch', 300);
  });

  d.addEventListener('sws:action-error', event => {
    const el = event.detail && event.detail.source;
    if (!el || prefersReducedMotion()) return;

    animate(
      el,
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(4px)' },
        { transform: 'translateX(-2px)' },
        { transform: 'translateX(0)' }
      ],
      { duration: 245, easing: 'ease-out' }
    );
  });

  d.addEventListener('click', event => {
    if (prefersReducedMotion()) return;

    const galleryControl = safeClosest(
      event.target,
      '[data-gallery-thumb],[data-gallery-prev],[data-gallery-next],.gallery-arrow,.product-thumb'
    );

    if (galleryControl) {
      w.requestAnimationFrame(() => {
        w.requestAnimationFrame(() => {
          const gallery = galleryControl.closest('[data-product-gallery],.product-gallery');
          const active = gallery && $('[data-gallery-slide].is-active,.product-gallery__slide.is-active', gallery);
          const counter = gallery && $('[data-gallery-current]', gallery);

          replay(active, 'sws-gallery-pop', 270);
          replay(counter, 'sws-count-pop', 350);
        });
      });
    }


    const qty = safeClosest(
      event.target,
      '[data-qty-plus],[data-qty-minus],[data-cart-plus],[data-cart-minus]'
    );
    if (qty) replay(qty, 'sws-qty-pop', 230);

    if (!hasFinePointer()) {
      const card = safeClosest(event.target, '.category-card,.product-card');
      if (card) replay(card, 'sws-card-tap', 285);

      const primary = safeClosest(event.target, '.btn--primary,.quick-add');
      if (primary) replay(primary, 'sws-tap-sheen', 360);

      const icon = safeClosest(event.target, '.icon-btn,.gallery-arrow,.gallery-zoom');
      if (icon) replay(icon, 'sws-icon-tap', 250);
    }
  }, { passive: true });

  d.addEventListener(
    'toggle',
    event => {
      const details = safeClosest(event.target, 'details');
      if (!details || prefersReducedMotion()) return;

      if (details.open) {
        replay(details, 'sws-details-open', 280);
        microBurst(details.querySelector('summary') || details);
      }
    },
    true
  );

  function initSearchMotion(scope = d) {
    const results = $('[data-search-results]', scope);
    if (!results || results.dataset.swsMotionReady === '1' || prefersReducedMotion()) return;

    results.dataset.swsMotionReady = '1';

    const observer = new MutationObserver(() => {
      $$('.site-search__result', results)
        .slice(0, 6)
        .forEach((item, i) => {
          item.style.animationDelay = `${i * 18}ms`;
          replay(item, 'sws-search-pop', 300);
        });
    });

    observer.observe(results, { childList: true, subtree: true });
  }

  function syncMotionPreferences() {
    const reduced = prefersReducedMotion();
    const touch = hasCoarsePointer() || !hasFinePointer();


    root.classList.toggle('sws-reduced-motion', reduced);
    root.classList.toggle('sws-touch-device', touch);

    if (reduced) {
      $$('.sws-reveal').forEach(el => el.classList.add('is-visible'));
      activePresses.forEach((_, pointerId) => releasePress(pointerId));
    }
  }

  function bindMediaChange(query) {
    if (!query) return;
    if (typeof query.addEventListener === 'function') query.addEventListener('change', syncMotionPreferences);
    else if (typeof query.addListener === 'function') query.addListener(syncMotionPreferences);
  }

  bindMediaChange(reduceQuery);
  bindMediaChange(fineQuery);
  bindMediaChange(coarseQuery);

  function boot(scope = d) {
    syncMotionPreferences();
    initHeader();
    registerReveals(scope);
    initSearchMotion(scope);
    showPageWink();
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  d.addEventListener('shopify:section:load', event => boot(event.target || d));
})();

